import { createAsyncThunk } from "@reduxjs/toolkit";
import { getFiltersByHash, sendRequest } from "@/ts/retrieve-data";
import { updateSelectedDateRange } from "@/features/selectedDataSlice";
import { updateRevstackLayoutFilters, updateMainFilters, appendRelativeFilters, applyAdditionalFiltersFromHash } from "@/features/revstackWidgetsSlice";
import { updateRevstackChannel } from "@/features/channelSlice";
import { updateActiveClientId, updateClientIdOnChannelChange } from "@/features/clientDataSlice";
import {
    IAdditionalFilterData,
    IApplyHashFiltersToStateCurrentState,
    IChannelValidationResult,
    IFilterHashPayload,
    IFilterHashResponse,
    ILayoutFiltersValidationResult,
    IMainFiltersData,
    IMainFiltersValidationResult,
    IOpenPageInNewTabParams,
    IOverrideFilterData,
    IRevstackChannels,
    IRevstackFilterOption,
    IRevstackLayoutFilters,
    IValidateLayoutFiltersState,
    SubModule,
    TOptionsString,
} from "../common-types";
import { showToast } from "../modals";
import { normalizeRevstackLayoutFilters } from "./helpers";
import { ALLOWED_MODULES_FOR_HASH_FILTERS, DATE_PICKER_PRESET_PERIODS } from "../constants";
import { generateSHA1Hash, getActiveModule } from "../utils";
import store from "@/store";
import { BrandTypes } from "@/components/widgets/filter-widgets/TopLevelFilters";

export const getRevstackSelectedFilterOptions = (): Record<string, Array<{ label: string, value: number }>> => {
    const mainFilters = store.getState().mainFilters?.value;
    const mainFiltersData = store.getState().mainFiltersData?.value;

    if (!mainFilters || !mainFiltersData) return {};

    const selectedOptions: Record<string, Array<{ label: string, value: number }>> = {};

    Object.keys(mainFilters).forEach((filterKey: string) => {
        // Skip the 'selected' key if it exists
        if (filterKey === 'selected') return;

        const filterIds = mainFilters[filterKey];
        const filterData = mainFiltersData[filterKey];

        if (Array.isArray(filterIds) && Array.isArray(filterData)) {
            selectedOptions[filterKey] = [];

            filterIds.forEach((filterId: number) => {
                // Skip negative values and -1
                if (filterId > 0) {
                    const filterItem = filterData.find((item: IRevstackFilterOption) => item.value === filterId);
                    if (filterItem && filterItem.label) {
                        selectedOptions[filterKey].push({
                            label: filterItem.label,
                            value: filterId
                        });
                    }
                }
            });
        }
    });

    return selectedOptions;
};

export const openPageInNewTab = async ({
    platform,
    module,
    queryParams,
    mainFilters
}: IOpenPageInNewTabParams) => {
    const additionalData: IAdditionalFilterData = {
        dateRange: queryParams.dateRange,
        channel: queryParams.channel as number[],
        client: queryParams.client as number,
        skus: queryParams.sku ? [queryParams.sku] : undefined,
        priceType: queryParams.revstackLayoutFilters?.priceType?.selected,
        metricType: queryParams.revstackLayoutFilters?.metricType?.selected,
        advancedFilters: queryParams.advancedFilters as Record<string, number[]>,
    };

    const filterPayload = prepareFilterPayload(mainFilters, additionalData);
    const hash = await createFilterHash(platform, module, filterPayload);

    if (hash) {
        const url = `/${platform}/${module}?filters=${hash}`;
        window.open(url, "_blank");
    }
}

export function prepareFilterPayload(
    draftSelected: Record<string, number[] | string[]>,
    additionalData?: IAdditionalFilterData
): IFilterHashPayload {
    // Filter out ALL_* markers, keep only numeric IDs
    const numericBrands = (draftSelected.brands || []).filter(
        (id: number | string) => typeof id === 'number'
    );
    const numericCategories = (draftSelected.categories || []).filter(
        (id: number | string) => typeof id === 'number'
    );
    const numericSubcategories = (draftSelected.subcategories || []).filter(
        (id: number | string) => typeof id === 'number'
    );

    const state = store.getState();

    const payload: IFilterHashPayload = {
        brands: numericBrands,
        categories: numericCategories,
        subcategories: numericSubcategories,
        client: state.activeClientId.value,
    };

    // Add optional fields if provided
    if (additionalData?.dateRange) {
        payload.dateRange = {
            current_period: additionalData.dateRange.dateRange
                ? [
                    additionalData.dateRange.dateRange.start,
                    additionalData.dateRange.dateRange.end,
                ]
                : [],
            previous_period: additionalData.dateRange.compareDateRange
                ? [
                    additionalData.dateRange.compareDateRange.start,
                    additionalData.dateRange.compareDateRange.end,
                ]
                : [],
            selectedRange: additionalData.dateRange.selectedRange,
        };
    }

    if (additionalData?.channel) {
        payload.channel = additionalData.channel;
    }

    if (additionalData?.skus && additionalData.skus.length > 0) {
        payload.skus = additionalData.skus;
    }

    if (additionalData?.advancedFilters) {
        payload.advancedFilters = additionalData.advancedFilters;
    }

    if (additionalData?.priceType) {
        payload.priceType = additionalData.priceType;
    }

    if (additionalData?.metricType) {
        payload.metricType = additionalData.metricType;
    }

    return payload;
}

export async function createFilterHash(
    platform: string,
    module: string,
    filters: IFilterHashPayload
): Promise<string | null> {
    try {
        
        const hash = await generateSHA1Hash(JSON.stringify(filters));
        const response = await sendRequest({
            type: 'revstack/filters',
            method: 'POST',
            data: {
                hash,
                platform,
                module,
                filters,
            },
        }) as IFilterHashResponse;

        // Backend returns 'status' instead of 'success'
        if (response?.status) {
            return hash;
        } else {
            console.error('Failed to create filter hash:', response?.message);
            showToast('danger', 'Failed to save filter configuration');
            return null;
        }
    } catch (error) {
        console.error('Error creating filter hash:', error);
        showToast('danger', 'Error saving filter configuration');
        return null;
    }
}

export function updateUrlWithHash(hash: string, platform: string, module: string): void {
    const newUrl = `/${platform}/${module}?filters=${hash}`;
    window.history.replaceState({ filterHash: hash }, '', newUrl);
}

export function getHashFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const hash = urlParams.get('filters');

    // Validate that it's a valid SHA1 hash (40 hex characters)
    if (hash && /^[a-f0-9]{40}$/i.test(hash)) {
        return hash;
    } else {
        const { activeSection: platform, activeParentModule: parentModule, activeModule: module } = getActiveModule();
        removeHashFromUrl(platform, module);
        return null;
    }
}

export function hasFilterHashInUrl(): boolean {
    return getHashFromUrl() !== null;
}

export function removeHashFromUrl(platform: string, module: string): void {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('filters');
    
    const queryString = urlParams.toString();
    const newUrl = `/${platform}/${module}${queryString ? `?${queryString}` : ''}`;
    window.history.replaceState({}, '', newUrl);
}

const getFilterValue = <T>(
    overrideData: { resetFilters?: boolean } | undefined,
    overrideValue: T | undefined,
    stateValue: T,
    resetDefault: T
): T => {
    return overrideData?.resetFilters
        ? overrideValue ?? resetDefault
        : overrideValue ?? stateValue;
}


export async function applyCurrentFiltersWithHash(
    overrideData?: IOverrideFilterData
): Promise<string | null> {
    const state = store.getState();

    const mainFilters = getFilterValue(
        overrideData,
        overrideData?.mainFilters,
        state.mainFilters.value,
        {
            categories: [],
            subcategories: [],
            brands: [],
            skus: [],
            channels: [],
        }
    );

    const dateRange = overrideData?.dateRange || state.selectedDateRange.value;

    const channel = getFilterValue(
        overrideData,
        overrideData?.channel,
        state.revstackChannel.value,
        []
    );

    const client = overrideData?.client || state.activeClientId.value;

    const revstackLayoutFilters = (state as { revstackLayoutFilters: { value: IRevstackLayoutFilters } }).revstackLayoutFilters?.value;
    const priceType = overrideData?.priceType || revstackLayoutFilters?.priceType?.selected;
    const metricType = overrideData?.metricType || revstackLayoutFilters?.metricType?.selected;

    const skus = getFilterValue(
        overrideData,
        overrideData?.skus,
        state.relativeFilters.value.skus,
        []
    );
    
    const { activeSection: platform, activeModule: module } = getActiveModule();
    
    const additionalFiltersState = state.additionalFilters?.value?.state;
    /*
    !Note[DND]: shouldExcludeAdvancedFilters
        Exclude advanced filters when in Product Analysis module with SKUs selected
        In future we might get a case where sku and advanced filters both might be required, then we can either plan to reset the advanced filters based on some attribute
    */
    const shouldExcludeAdvancedFilters = module === SubModule.PRODUCT_ANALYSIS && skus.length > 0;
    const advancedFilters = overrideData?.advancedFilters || (shouldExcludeAdvancedFilters ? {} : additionalFiltersState?.appliedFilters);
    
    const additionalData = {
        dateRange,
        channel,
        client,
        skus,
        advancedFilters,
        priceType,
        metricType,
    } as IAdditionalFilterData;
    
    return applyFiltersWithHash(mainFilters, platform, module, additionalData);
}

export async function applyFiltersWithHash(
    draftSelected: Record<string, number[] | string[]>,
    platform: string,
    module: string,
    additionalData?: IAdditionalFilterData
): Promise<string | null> {
    
    const filterPayload = prepareFilterPayload(draftSelected, additionalData);
    
    const hash = await createFilterHash(platform, module, filterPayload);
    
    if (hash) {
        updateUrlWithHash(hash, platform, module);
        return hash;
    }
    
    return null;
}

export const loadFiltersFromUrlAsync = async () => {
    const hash = getHashFromUrl();

    if (!hash) {
        return null;
    }

    const filters = await getFiltersByHash(hash);

    return filters;
}

export const loadFiltersFromUrl = createAsyncThunk(
    "filters/loadFromUrl",
    async (_, { rejectWithValue }) => {
        try {
            return await loadFiltersFromUrlAsync();
        } catch (error: any) {
            console.error('Error loading filters from URL:', error);
            return rejectWithValue(error.message || 'Failed to load filters from URL');
        }
    }
);

/**
 * !Note[DND]:
 * Loads filters from URL hash and applies client/channel filters immediately
 * This function is designed for app initialization to ensure proper filter setup
 * before the app renders
 */

export const initializeFiltersFromUrl = async (): Promise<void> => {
    const activeModule = getActiveModule().activeModule;

    if(!ALLOWED_MODULES_FOR_HASH_FILTERS.includes(activeModule as SubModule)) return;

    if (!hasFilterHashInUrl()) return;

    const result = await store.dispatch(loadFiltersFromUrl());
    
    // Apply client and channel immediately after loading, before app renders
    if (result.payload) {
        const hashFilters = result.payload as IFilterHashPayload;
        if (hashFilters.client) {
            store.dispatch(updateClientIdOnChannelChange(hashFilters.client));
            store.dispatch(updateActiveClientId(hashFilters.client));
        }
        if (hashFilters.channel && hashFilters.channel.length > 0) {
            store.dispatch(updateRevstackChannel(hashFilters.channel));
        }
        if (hashFilters.advancedFilters && Object.keys(hashFilters.advancedFilters).length > 0) {
            store.dispatch(applyAdditionalFiltersFromHash(hashFilters.advancedFilters));
        }
    }
};

const validateMainFilters = (
    hashFilters: Pick<IFilterHashPayload, 'categories' | 'subcategories' | 'brands'>,
    mainFiltersData: IMainFiltersData | null | undefined
) => {
    const result: IMainFiltersValidationResult = {
        categories: { valid: hashFilters.categories || [], invalid: [] },
        subcategories: { valid: hashFilters.subcategories || [], invalid: [] },
        brands: { valid: hashFilters.brands || [], invalid: [] }
    };

    if (!mainFiltersData) {
        return result;
    }

    // Validate categories
    // Only validate if metadata is available (length > 0)
    // If metadata lists are empty, we assume data hasn't been fetched and allow all filters
    if (hashFilters.categories && mainFiltersData.categories && mainFiltersData.categories.length > 0) {
        const availableCategoryIds = new Set(
            mainFiltersData.categories.map((c: IRevstackFilterOption) => c.value)
        );
        result.categories.valid = [];
        result.categories.invalid = [];
        for (const cat of hashFilters.categories) {
            if (availableCategoryIds.has(cat)) {
                result.categories.valid.push(cat);
            } else {
                result.categories.invalid.push(cat);
            }
        }
    }

    // Validate subcategories
    if (hashFilters.subcategories && mainFiltersData.subcategories && mainFiltersData.subcategories.length > 0) {
        const availableSubcategoryIds = new Set(
            mainFiltersData.subcategories.map((s: IRevstackFilterOption) => s.value)
        );
        result.subcategories.valid = [];
        result.subcategories.invalid = [];
        for (const sub of hashFilters.subcategories) {
            if (availableSubcategoryIds.has(sub)) {
                result.subcategories.valid.push(sub);
            } else {
                result.subcategories.invalid.push(sub);
            }
        }
    }

    // Validate brands
    if (hashFilters.brands && mainFiltersData.brands && mainFiltersData.brands.length > 0) {
        const availableBrandIds = new Set(
            mainFiltersData.brands.map((b: IRevstackFilterOption) => b.value)
        );
        result.brands.valid = [];
        result.brands.invalid = [];
        for (const brand of hashFilters.brands) {
            if (availableBrandIds.has(brand)) {
                result.brands.valid.push(brand);
            } else {
                result.brands.invalid.push(brand);
            }
        }
    }

    // Apply hierarchical invalidation rules
    const hasValidCategories = result.categories.valid.length > 0;
    const hasValidSubcategories = result.subcategories.valid.length > 0;

    // Rule 1: If all categories are invalid, invalidate all subcategories and brands
    if (hashFilters.categories && hashFilters.categories.length > 0 && !hasValidCategories) {
        result.subcategories.valid = [];
        result.subcategories.invalid = [...(hashFilters.subcategories || [])];
        result.brands.valid = [];
        result.brands.invalid = [...(hashFilters.brands || [])];
    }
    // Rule 2: If all subcategories are invalid, invalidate all brands
    else if (hashFilters.subcategories && hashFilters.subcategories.length > 0 && !hasValidSubcategories) {
        result.brands.valid = [];
        result.brands.invalid = [...(hashFilters.brands || [])];
    }

    return result;
};

const validateChannels = (channelIds: number[], revstackChannels: IRevstackChannels[]) => {
    const result: IChannelValidationResult = {
        channels: { valid: [...channelIds], invalid: [] }
    };

    if (!revstackChannels || revstackChannels.length === 0) {
        return result;
    }

    const allChannelIds = revstackChannels.map((channel: IRevstackChannels) => channel.id);
    result.channels.valid = [];
    result.channels.invalid = [];
    for (const channelId of channelIds) {
        if (allChannelIds.includes(channelId)) {
            result.channels.valid.push(channelId);
        } else {
            result.channels.invalid.push(channelId);
        }
    }

    return result;
};

const validateLayoutFilters = (
    hashFilters: Pick<IFilterHashPayload, 'priceType' | 'metricType'>,
    currentState: IValidateLayoutFiltersState
) => {
    const result: ILayoutFiltersValidationResult = {};

    // Validate priceType
    if (hashFilters.priceType) {
        const priceTypeWithOptions = currentState.revstackLayoutFilters.priceType as { options?: TOptionsString[] } | undefined;
        if (priceTypeWithOptions?.options) {
            const availablePriceTypes = priceTypeWithOptions.options.map(
                (option: TOptionsString) => option.value
            );
            result.priceType = availablePriceTypes.includes(hashFilters.priceType)
                ? hashFilters.priceType
                : null;
        } else {
            // Apply anyway if validation data not available yet
            result.priceType = hashFilters.priceType;
        }
    }

    // Validate metricType
    if (hashFilters.metricType) {
        if (currentState.revstackLayoutFilters.metricType?.options) {
            const availableMetricTypes = currentState.revstackLayoutFilters.metricType.options.map(
                (option: TOptionsString & { options?: TOptionsString[] }) => option.value
            );
            result.metricType = availableMetricTypes.includes(hashFilters.metricType)
                ? hashFilters.metricType
                : null;
        } else {
            // Apply anyway if validation data not available yet
            result.metricType = hashFilters.metricType;
        }
    }

    return result;
};

/**
 * Applies hash filters from URL to the Redux state with validation
 * Orchestrates various validation helpers for different filter types
 */
export const applyHashFiltersToState = (
    hashFilters: IFilterHashPayload,
    currentState: IApplyHashFiltersToStateCurrentState
): void => {
    const invalidFilters: Record<string, number[] | string | undefined> = {};

    if (hashFilters.client) {
        store.dispatch(updateClientIdOnChannelChange(hashFilters.client));
        store.dispatch(updateActiveClientId(hashFilters.client));
    }

    // Validate and apply channel filters first
    if (hashFilters.channel && hashFilters.channel.length > 0) {
        const revstackChannels = store.getState().revstackChannelsList.value;
        const channelValidation = validateChannels(hashFilters.channel, revstackChannels);

        if (channelValidation.channels.valid.length > 0) {
            store.dispatch(updateRevstackChannel(channelValidation.channels.valid));
        }

        if (channelValidation.channels.invalid.length > 0) {
            invalidFilters.channels = channelValidation.channels.invalid;
        }
    }

    if (hashFilters.categories || hashFilters.subcategories || hashFilters.brands) {
        const mainFiltersData = store.getState().mainFiltersData?.value;
        const mainFiltersValidation = validateMainFilters(
            {
                categories: hashFilters.categories,
                subcategories: hashFilters.subcategories,
                brands: hashFilters.brands
            },
            mainFiltersData
        );

        let finalCategories = mainFiltersValidation.categories.valid;
        let finalSubcategories = mainFiltersValidation.subcategories.valid;
        let finalBrands = mainFiltersValidation.brands.valid;

        // If no valid categories after filter change, default to category with highest UPD
        // mainFiltersData.selected contains categories/subcategories/brands based on is_most_relevant_category
        // (same logic as formatMainFilters in formatters.ts)
        if (hashFilters.categories && hashFilters.categories.length > 0 && finalCategories.length === 0) {
            if (mainFiltersData?.selected?.categories && mainFiltersData.selected.categories.length > 0) {
                // Use the category with highest UPD (is_most_relevant_category = true)
                // This ensures consistent behavior across the app
                finalCategories = [...mainFiltersData.selected.categories];
                finalSubcategories = [...(mainFiltersData.selected.subcategories || [])];
                finalBrands = [...(mainFiltersData.selected.brands || [])];
            } else if (mainFiltersData?.categories && mainFiltersData.categories.length > 0) {
                // Fallback to first available category if no UPD-based selection exists
                const firstCategoryId = mainFiltersData.categories[0].value;
                finalCategories = [firstCategoryId];
                
                // Get all subcategories for this category
                finalSubcategories = mainFiltersData.subcategories
                    ?.filter((subcat: any) => subcat.category === firstCategoryId)
                    .map((subcat: any) => subcat.value) || [];
                
                // Get all self-brands (mybrand) for these subcategories
                const subcategoryIds = new Set(finalSubcategories);
                finalBrands = mainFiltersData.brands
                    ?.filter((brand: any) => 
                        brand.brandType === BrandTypes.MY_BRAND && 
                        brand.subcategories?.some((subId: any) => subcategoryIds.has(subId))
                    )
                    .map((brand: any) => brand.value) || [];
            }
        }

        const mainFiltersPayload = {
            ...store.getState().mainFilters.value,
            categories: finalCategories,
            subcategories: finalSubcategories,
            brands: finalBrands,
        };
        store.dispatch(updateMainFilters(mainFiltersPayload));

        if (mainFiltersValidation.categories.invalid.length > 0) {
            invalidFilters.categories = mainFiltersValidation.categories.invalid;
        }
        if (mainFiltersValidation.subcategories.invalid.length > 0) {
            invalidFilters.subcategories = mainFiltersValidation.subcategories.invalid;
        }
        if (mainFiltersValidation.brands.invalid.length > 0) {
            invalidFilters.brands = mainFiltersValidation.brands.invalid;
        }
    }

    // Apply date range filters (no validation needed for date ranges)
    if (hashFilters.dateRange) {
        const dateRangePayload = {
            dateRange: {
                start: hashFilters.dateRange.current_period?.[0] || '',
                end: hashFilters.dateRange.current_period?.[1] || '',
            },
            compareDateRange: {
                start: hashFilters.dateRange.previous_period?.[0] || '',
                end: hashFilters.dateRange.previous_period?.[1] || '',
            },
            selectedRange: (hashFilters.dateRange.selectedRange || currentState.selectedDateRange.selectedRange || DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS) as typeof DATE_PICKER_PRESET_PERIODS[keyof typeof DATE_PICKER_PRESET_PERIODS],
        };
        store.dispatch(updateSelectedDateRange(dateRangePayload));
    }

    // Validate and apply layout filters (priceType, metricType)
    if (hashFilters.priceType || hashFilters.metricType) {
        const layoutFiltersValidation = validateLayoutFilters(
            { priceType: hashFilters.priceType, metricType: hashFilters.metricType },
            currentState
        );

        const updatedLayoutFilters = { ...currentState.revstackLayoutFilters };
        let hasChanges = false;

        // Apply priceType
        if (layoutFiltersValidation.priceType) {
            updatedLayoutFilters.priceType = {
                ...currentState.revstackLayoutFilters.priceType,
                selected: layoutFiltersValidation.priceType,
            };
            hasChanges = true;
        } else if (layoutFiltersValidation.priceType === null && hashFilters.priceType) {
            invalidFilters.priceType = hashFilters.priceType;
        }

        // Apply metricType
        if (layoutFiltersValidation.metricType) {
            updatedLayoutFilters.metricType = {
                ...currentState.revstackLayoutFilters.metricType,
                selected: layoutFiltersValidation.metricType,
            };
            hasChanges = true;
        } else if (layoutFiltersValidation.metricType === null && hashFilters.metricType) {
            invalidFilters.metricType = hashFilters.metricType;
        }

        if (hasChanges) {
            const normalizedFilters = normalizeRevstackLayoutFilters(updatedLayoutFilters);
            store.dispatch(updateRevstackLayoutFilters(normalizedFilters));
        }
    }

    if (hashFilters.skus && hashFilters.skus.length > 0) {
        store.dispatch(appendRelativeFilters({ skus: hashFilters.skus }));
    }

    if (hashFilters.advancedFilters && Object.keys(hashFilters.advancedFilters).length > 0) {
        // Ensure the filters are in the correct format (Record<string, number[]>)
        const normalizedAdvancedFilters: Record<string, number[]> = {};
        Object.entries(hashFilters.advancedFilters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                // Ensure all values in the array are numbers
                const numericValues = value.filter((v: any) => typeof v === 'number' || !isNaN(Number(v)))
                    .map((v: any) => typeof v === 'number' ? v : Number(v));
                if (numericValues.length > 0) {
                    normalizedAdvancedFilters[key] = numericValues;
                }
            }
        });
        
        if (Object.keys(normalizedAdvancedFilters).length > 0) {
            store.dispatch(applyAdditionalFiltersFromHash(normalizedAdvancedFilters));
        }
    }

    if (Object.keys(invalidFilters).length > 0) {
        showToast('warning', "Some filters from the URL are no longer available and were excluded");
    }
}